// The clocks of a game: the clock after each move, read from the PGN's
// `[%clk h:mm:ss(.f)]` comments (chess.com writes one after every move), the
// seconds the mover had left once the move was made. Index 0 is ply 1; a move
// without a clock comment is undefined, and a PGN without any gives an empty
// list. The review's loader stores them on `ReviewGame.clocks`, with the
// initial time from the time control (`initialClockOf`), so the board shows
// each player's clock at the position shown and the dashboard's facts read the
// same numbers (`api-insights`' `ScannedGame.clockAfter`).

const CLOCK = /\[%clk\s+(\d+):(\d{1,2}):(\d{1,2}(?:\.\d+)?)\]/;

/** Seconds from `h:mm:ss(.f)`. */
function seconds(h: string, m: string, s: string): number {
  return Number(h) * 3600 + Number(m) * 60 + Number(s);
}

/** The movetext: everything after the header block. */
function movetext(pgn: string): string {
  const lines = pgn.split(/\r?\n/);
  let i = 0;
  while (i < lines.length && (lines[i].startsWith('[') || lines[i].trim() === '')) i++;
  return lines.slice(i).join(' ');
}

/** Seconds left on the mover's clock after each ply, by ply - 1. */
export function clocksOf(pgn: string): Array<number | undefined> {
  const out: Array<number | undefined> = [];
  if (!pgn.includes('%clk')) return out;
  const text = movetext(pgn);
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    if (c === '{') {
      const end = text.indexOf('}', i + 1);
      const m = CLOCK.exec(text.slice(i + 1, end < 0 ? text.length : end));
      if (m && out.length > 0) out[out.length - 1] = seconds(m[1], m[2], m[3]);
      i = end < 0 ? text.length : end + 1;
      continue;
    }
    if (c === '(') {
      // A variation, nested ones included: not the game's moves.
      let depth = 1;
      i++;
      while (i < text.length && depth > 0) {
        if (text[i] === '(') depth++;
        else if (text[i] === ')') depth--;
        i++;
      }
      continue;
    }
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    let j = i;
    while (j < text.length && !/[\s{}()]/.test(text[j])) j++;
    const token = text.slice(i, j);
    i = j;
    // Move numbers ("12." "12..."), NAGs ("$1") and results are not moves.
    if (/^\d+\.+$/.test(token) || token.startsWith('$') || /^(1-0|0-1|1\/2-1\/2|\*)$/.test(token)) continue;
    if (token.replace(/^\d+\.+/, '')) out.push(undefined);
  }
  return out;
}

/**
 * The seconds each player starts with under a stored time control: "5+0"
 * (minutes, plus an increment in seconds), "30s+0" (seconds); undefined for
 * "daily", none, or a form that is not understood.
 */
export function initialClockOf(timeControl: string | undefined): number | undefined {
  if (!timeControl || timeControl === 'daily') return undefined;
  const base = timeControl.split('+')[0].trim();
  const seconds = base.endsWith('s') ? Number(base.slice(0, -1)) : Number(base) * 60;
  return Number.isFinite(seconds) && base !== '' && seconds > 0 ? seconds : undefined;
}
